// @ts-check
import orderBy from 'lodash/orderBy';
/**
 * @typedef CreateFacetOptions
 * @property {string} stateKey The key of the facet within the state object.
 * @property {string} [sortBy] The order the filter facet values should be sorted.
 * @property {'asc' | 'desc'} [sortDirection] The direction to sort the filter results in.
 * @property {string} [configKey] The key in the config object that contains the flag to enable/disable the filter.
 *
 * @typedef Facet A facet definition.
 * @property {object} state
 * @property {(facetValues: object, item: object) => object} reducer
 * @property {(state: object) => (item: object) =>  boolean} matches
 * @property {any} initialValue
 *
 * @typedef FacetDefaults The overrides for a facet.
 * @property {object} [state]
 * @property {(facetValues: object, item: object) => object} [reducer]
 * @property {(state: object) => (item: object) =>  boolean} [matches]
 * @property {any} [initialValue]
 */

/**
 *
 * @param {FacetDefaults} facet The base facet data.
 * @param {CreateFacetOptions} options The facet configuration.
 * @returns {(config: object, state: object) =>  Facet | false} The facet definition creator.
 */
export default function createFacet(
  facet,
  { stateKey, sortBy = 'count', sortDirection = 'desc', configKey }
) {
  return (config, state) => {
    if (!stateKey) return false;
    if (configKey && config && !config[configKey]) return false;
    return {
      // Define the state that is used for filtering the items before reducing to individual values.
      state: { ...state, [stateKey]: [] },

      /**
       * The reducer for the facet values.
       * @abstract Override this function.
       */
      reducer: (facetValues, _item) => facetValues,

      // The initial value for the reducer.
      // If a filter is set on the keys: keep that value in the facets even with 0 results.
      initialValue:
        facet.initialValue ||
        (state[stateKey] || []).reduce((initialValue, facetValue) => {
          initialValue[facetValue] = 0;
          return initialValue;
        }, {}),

      // Sort and group the facets before displaying them.
      prepare: (facetValues) =>
        facetValues &&
        orderBy(
          Object.entries(facetValues)
            .filter(([key]) => Boolean(key))
            .map(([value, count]) => ({ value, count })),
          sortBy,
          sortDirection
        ),

      // Tests if an item matches a certain filter.
      matches: (_state) => (_item) => true,

      ...facet,
    };
  };
}
